/* eslint-disable @wordpress/i18n-no-variables */
import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import Visibility from '@components/visibility';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import { DEFAULT_CSS_CLASS } from '@constants';
import classnames from 'classnames';
import './editor.scss';
import { setUniqueIdAttribute } from '@helpers/utils.js';
import Label from '@components/block-label';
import {
  __experimentalDivider as Divider,
  __experimentalNumberControl as NumberControl,
} from '@wordpress/components';
import {
  ContactFormSettings,
  HubspotSettings,
  MarketoSettings,
  DownloadSettings,
  PopupSettings,
  RedirectSettings,
} from './_partials';
import TagSelector from '@components/tag-selector';

const {
  i18n: { __ },
  element: { useEffect },
  blockEditor: { useBlockProps, useInnerBlocksProps },
  components: { PanelBody, SelectControl, TextControl, Notice },
} = wp;

const InspectorGeneral = (props) => {
  const { attributes, setAttributes } = props;
  const {
    blockVisibility,
    label,
    formType,
    responseType,
    responseMessage,
    cookieValidity,
    responseHeading,
    headingTag,
  } = attributes;

  return (
    <InspectorControlsGeneral>
      <Label {...{ label, setAttributes }} />
      <Visibility {...{ blockVisibility, setAttributes }} />
      <PanelBody
        title={__('Embedded Form Details', 'b3rg')}
        initialOpen={true}
        className="form-details"
      >
        <SelectControl
          label={__('Form Type', 'b3rg')}
          value={formType}
          options={[
            { label: 'Select an Option', value: '' },
            { label: 'Contact Form 7', value: 'cf7' },
            { label: 'Hubspot', value: 'hubspot' },
            { label: 'Marketo', value: 'marketo' },
          ]}
          onChange={(formType) => {
            setAttributes({ formType });
          }}
        />
        {formType === 'cf7' && (
          <ContactFormSettings
            setAttributes={setAttributes}
            attributes={attributes}
          />
        )}
        {formType === 'hubspot' && (
          <HubspotSettings
            setAttributes={setAttributes}
            attributes={attributes}
          />
        )}
        {formType === 'marketo' && (
          <MarketoSettings
            setAttributes={setAttributes}
            attributes={attributes}
          />
        )}
      </PanelBody>
      {(formType === 'hubspot' ||
        formType === 'marketo' ||
        formType === 'cf7') && (
        <PanelBody
          title={__('Response Options', 'b3rg')}
          initialOpen={true}
          className="response-options"
        >
          {formType !== 'cf7' && (
            <>
              <TextControl
                label={__('Response Message', 'b3rg')}
                value={responseMessage}
                onChange={(responseMessage) => {
                  setAttributes({ responseMessage });
                }}
              />
              <TextControl
                label={__('Response Heading', 'embedded-forms')}
                value={responseHeading}
                onChange={(responseHeading) => {
                  setAttributes({ responseHeading });
                }}
              />
              <TagSelector
                label={__('Response Heading Tag', 'embedded-forms')}
                value={headingTag}
                onChange={(headingTag) => {
                  setAttributes({ headingTag });
                }}
              />
            </>
          )}

          <SelectControl
            label={__('Success Action', 'b3rg')}
            value={responseType}
            options={[
              { label: 'Custom URL', value: 'redirect' },
              { label: 'Download', value: 'download' },
              { label: 'Video Popup', value: 'popup' },
              {
                label: 'View Resource on the same page',
                value: 'resource-same-page',
              },
            ]}
            onChange={(responseType) => {
              setAttributes({ responseType });
            }}
          />
          <Divider />
          {responseType === 'download' && (
            <DownloadSettings
              setAttributes={setAttributes}
              attributes={attributes}
            />
          )}

          {responseType === 'redirect' && (
            <RedirectSettings
              setAttributes={setAttributes}
              attributes={attributes}
            />
          )}

          {responseType === 'resource-same-page' && (
            <NumberControl
              label={__('Form Cookie Validity (Days)', 'b3rg')}
              value={cookieValidity}
              min={1}
              max={365}
              onChange={(cookieValidity) => {
                setAttributes({ cookieValidity: parseInt(cookieValidity) });
              }}
            />
          )}
          {responseType === 'popup' && (
            <PopupSettings
              setAttributes={setAttributes}
              attributes={attributes}
            />
          )}
        </PanelBody>
      )}
    </InspectorControlsGeneral>
  );
};

const InspectorAppearance = (props) => {
  const {
    attributes: { blockClassNames },
    setAttributes,
  } = props;

  const defaultBlockCssClass = DEFAULT_CSS_CLASS.EMBEDDED_FORMS;
  return (
    <InspectorControlsAppearance>
      <ClassDropdown
        {...{
          blockClassNames,
          setAttributes,
          blockClassPrefix: defaultBlockCssClass,
        }}
      />
    </InspectorControlsAppearance>
  );
};
const Edit = (props) => {
  const { name, attributes, setAttributes } = props;
  const { blockVisibility, blockClassNames, formType, embeddedFormId } =
    attributes;

  useEffect(() => {
    setUniqueIdAttribute(name, 'embeddedFormId', attributes, setAttributes);
  }, [name, attributes, setAttributes]);

  const defaultBlockCssClass = DEFAULT_CSS_CLASS.EMBEDDED_FORMS;
  const defaultBlockDynamicCssClass = `${defaultBlockCssClass}`;
  const classNames = getBlockClassNames(blockClassNames);

  const blockProps = useBlockProps({
    className: classnames({
      [`${defaultBlockCssClass}`]: true,
      [`${defaultBlockDynamicCssClass}`]: defaultBlockDynamicCssClass,
      [`${classNames}`]: classNames,
      [`e-emf-${embeddedFormId}`]: true,
      disable: !blockVisibility,
    }),
  });

  const innerBlocksProps = useInnerBlocksProps(blockProps, {});

  return (
    <>
      <InspectorTabs>
        <InspectorGeneral {...props} />
        <InspectorAppearance {...props} />
      </InspectorTabs>
      <div {...innerBlocksProps}>
        <Notice
          status={`${formType ? 'warning' : 'info'}`}
          isDismissible={false}
        >
          {__(
            `Embedded Forms – ${
              formType
                ? 'Preview mode is not available for the selected form type'
                : 'Enter form data from the settings panel.'
            }`,
            'b3rg',
          )}
        </Notice>
      </div>
    </>
  );
};

export default Edit;
